import { all, call, put, takeLatest } from 'redux-saga/effects';
import { api } from '../../configurations/api';
import { sandboxOfficeHoursUrl } from '../../configurations/api/url';
import { SentryCapture } from '../../analytics/Sentry';
import { httpRequest } from '../types';
import { destroyOneCache, setCache } from '../cache/action';
import { CACHE_TYPE, type NotUniqueCacheValue } from '../cache/types';
import { getNotUniqueCacheByKey, isUseCacheEnabled } from '../cache/saga';
import {
    bookTeacherOfficeHours,
    bookTeacherOfficeHoursFail,
    bookTeacherOfficeHoursSuccess,
    cancelMyHostOfficeHourBooking,
    cancelMyHostOfficeHourBookingFail,
    cancelMyHostOfficeHourBookingSuccess,
    cancelMyStudentOfficeHourBooking,
    cancelMyStudentOfficeHourBookingFail,
    cancelMyStudentOfficeHourBookingSuccess,
    fetchMyHostOfficeHourBookings,
    fetchMyHostOfficeHourBookingsFail,
    fetchMyHostOfficeHourBookingsSuccess,
    fetchMyOfficeHours,
    fetchMyOfficeHoursFail,
    fetchMyOfficeHoursSuccess,
    fetchMyStudentOfficeHourBookings,
    fetchMyStudentOfficeHourBookingsFail,
    fetchMyStudentOfficeHourBookingsSuccess,
    fetchOfficeHoursGoogleAuthorizeUrl,
    fetchOfficeHoursGoogleAuthorizeUrlFail,
    fetchOfficeHoursGoogleAuthorizeUrlSuccess,
    fetchTeacherOfficeHours,
    fetchTeacherOfficeHoursAvailability,
    fetchTeacherOfficeHoursAvailabilityFail,
    fetchTeacherOfficeHoursAvailabilitySuccess,
    fetchTeacherOfficeHoursFail,
    fetchTeacherOfficeHoursSuccess,
    requestMyStudentOfficeHourRefund,
    requestMyStudentOfficeHourRefundFail,
    requestMyStudentOfficeHourRefundSuccess,
    rescheduleMyStudentOfficeHourBooking,
    rescheduleMyStudentOfficeHourBookingFail,
    rescheduleMyStudentOfficeHourBookingSuccess,
    upsertMyOfficeHours,
    upsertMyOfficeHoursFail,
    upsertMyOfficeHoursSuccess,
} from './actions';
import { asBookingList, settle } from './sagaHelpers';

function* fetchMyOfficeHoursSaga({ resolve, reject }: any): Generator<any, void, any> {
    const cacheKey = 'mine';
    const useCache = yield* isUseCacheEnabled();
    const cache: NotUniqueCacheValue = yield* getNotUniqueCacheByKey(CACHE_TYPE.SANDBOX_MY_OFFICE_HOURS, cacheKey);
    if (useCache && cache?.value) {
        yield put(fetchMyOfficeHoursSuccess(cache.value));
        settle(resolve, reject, cache.value);
        return;
    }
    try {
        const res = yield call(api, `${sandboxOfficeHoursUrl}/mine`, httpRequest.GET, null, 2);
        const data = res?.data ?? res;
        yield put(fetchMyOfficeHoursSuccess(data));
        yield put(
            setCache({
                key: cacheKey,
                type: CACHE_TYPE.SANDBOX_MY_OFFICE_HOURS,
                value: data,
                isUnique: false,
            }),
        );
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchMyOfficeHoursFail(error));
        settle(resolve, reject, error, true);
    }
}

function* upsertMyOfficeHoursSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    try {
        const res = yield call(api, `${sandboxOfficeHoursUrl}/mine`, httpRequest.PUT, payload, 0);
        const data = res?.data ?? res;
        yield put(upsertMyOfficeHoursSuccess(data));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.SANDBOX_MY_OFFICE_HOURS }));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(upsertMyOfficeHoursFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchGoogleAuthorizeUrlSaga({ resolve, reject }: any): Generator<any, void, any> {
    try {
        const res = yield call(api, `${sandboxOfficeHoursUrl}/mine/google/authorize`, httpRequest.GET, null, 0);
        const data = res?.data ?? res;
        yield put(fetchOfficeHoursGoogleAuthorizeUrlSuccess(data));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchOfficeHoursGoogleAuthorizeUrlFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchTeacherOfficeHoursSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    try {
        const res = yield call(api, `${sandboxOfficeHoursUrl}/teacher/${payload}`, httpRequest.GET, null, 2);
        const data = res?.data ?? res;
        yield put(fetchTeacherOfficeHoursSuccess(data));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchTeacherOfficeHoursFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchTeacherAvailabilitySaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    try {
        const qs = new URLSearchParams({ date: payload.date });
        if (payload.timezone) qs.set('timezone', payload.timezone);
        const res = yield call(
            api,
            `${sandboxOfficeHoursUrl}/teacher/${payload.teacherId}/availability?${qs}`,
            httpRequest.GET,
            null,
            2,
        );
        const data = res?.data ?? res;
        yield put(fetchTeacherOfficeHoursAvailabilitySuccess(data));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchTeacherOfficeHoursAvailabilityFail(error));
        settle(resolve, reject, error, true);
    }
}

function* bookTeacherOfficeHoursSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    try {
        const res = yield call(
            api,
            `${sandboxOfficeHoursUrl}/teacher/${payload.teacherId}/book`,
            httpRequest.POST,
            payload.body,
            0,
        );
        const data = res?.data ?? res;
        yield put(bookTeacherOfficeHoursSuccess(data));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.SANDBOX_STUDENT_BOOKINGS }));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(bookTeacherOfficeHoursFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchHostBookingsSaga({ resolve, reject }: any): Generator<any, void, any> {
    const cacheKey = 'mine';
    const useCache = yield* isUseCacheEnabled();
    const cache: NotUniqueCacheValue = yield* getNotUniqueCacheByKey(CACHE_TYPE.SANDBOX_HOST_BOOKINGS, cacheKey);
    if (useCache && cache?.value) {
        const list = asBookingList(cache.value);
        yield put(fetchMyHostOfficeHourBookingsSuccess(list));
        settle(resolve, reject, list);
        return;
    }
    try {
        const res = yield call(api, `${sandboxOfficeHoursUrl}/mine/bookings`, httpRequest.GET, null, 2);
        const list = asBookingList(res?.data ?? res);
        yield put(fetchMyHostOfficeHourBookingsSuccess(list));
        yield put(
            setCache({
                key: cacheKey,
                type: CACHE_TYPE.SANDBOX_HOST_BOOKINGS,
                value: list,
                isUnique: false,
            }),
        );
        settle(resolve, reject, list);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchMyHostOfficeHourBookingsFail(error));
        settle(resolve, reject, error, true);
    }
}

function* cancelHostBookingSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    try {
        const res = yield call(
            api,
            `${sandboxOfficeHoursUrl}/mine/bookings/${payload.bookingId}/cancel`,
            httpRequest.PATCH,
            { reason: payload.reason },
            0,
        );
        const data = res?.data ?? res;
        yield put(cancelMyHostOfficeHourBookingSuccess(data));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.SANDBOX_HOST_BOOKINGS }));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(cancelMyHostOfficeHourBookingFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchStudentBookingsSaga({ resolve, reject }: any): Generator<any, void, any> {
    const cacheKey = 'mine';
    const useCache = yield* isUseCacheEnabled();
    const cache: NotUniqueCacheValue = yield* getNotUniqueCacheByKey(CACHE_TYPE.SANDBOX_STUDENT_BOOKINGS, cacheKey);
    if (useCache && cache?.value) {
        const list = asBookingList(cache.value);
        yield put(fetchMyStudentOfficeHourBookingsSuccess(list));
        settle(resolve, reject, list);
        return;
    }
    try {
        const res = yield call(api, `${sandboxOfficeHoursUrl}/me/bookings`, httpRequest.GET, null, 2);
        const list = asBookingList(res?.data ?? res);
        yield put(fetchMyStudentOfficeHourBookingsSuccess(list));
        yield put(
            setCache({
                key: cacheKey,
                type: CACHE_TYPE.SANDBOX_STUDENT_BOOKINGS,
                value: list,
                isUnique: false,
            }),
        );
        settle(resolve, reject, list);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchMyStudentOfficeHourBookingsFail(error));
        settle(resolve, reject, error, true);
    }
}

function* cancelStudentBookingSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    try {
        const res = yield call(
            api,
            `${sandboxOfficeHoursUrl}/me/bookings/${payload.bookingId}/cancel`,
            httpRequest.PATCH,
            { reason: payload.reason },
            0,
        );
        const data = res?.data ?? res;
        yield put(cancelMyStudentOfficeHourBookingSuccess(data));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.SANDBOX_STUDENT_BOOKINGS }));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(cancelMyStudentOfficeHourBookingFail(error));
        settle(resolve, reject, error, true);
    }
}

function* rescheduleStudentBookingSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    try {
        const res = yield call(
            api,
            `${sandboxOfficeHoursUrl}/me/bookings/${payload.bookingId}/reschedule`,
            httpRequest.PATCH,
            payload.body,
            0,
        );
        const data = res?.data ?? res;
        yield put(rescheduleMyStudentOfficeHourBookingSuccess(data));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.SANDBOX_STUDENT_BOOKINGS }));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(rescheduleMyStudentOfficeHourBookingFail(error));
        settle(resolve, reject, error, true);
    }
}

function* requestStudentRefundSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    try {
        const res = yield call(
            api,
            `${sandboxOfficeHoursUrl}/me/bookings/${payload.bookingId}/refund`,
            httpRequest.POST,
            { reason: payload.reason || 'Mentor did not attend' },
            0,
        );
        const data = res?.data ?? res;
        yield put(requestMyStudentOfficeHourRefundSuccess(data));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.SANDBOX_STUDENT_BOOKINGS }));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(requestMyStudentOfficeHourRefundFail(error));
        settle(resolve, reject, error, true);
    }
}

export default function* sandboxOfficeHoursSaga() {
    yield all([
        takeLatest(fetchMyOfficeHours as any, fetchMyOfficeHoursSaga),
        takeLatest(upsertMyOfficeHours as any, upsertMyOfficeHoursSaga),
        takeLatest(fetchOfficeHoursGoogleAuthorizeUrl as any, fetchGoogleAuthorizeUrlSaga),
        takeLatest(fetchTeacherOfficeHours as any, fetchTeacherOfficeHoursSaga),
        takeLatest(fetchTeacherOfficeHoursAvailability as any, fetchTeacherAvailabilitySaga),
        takeLatest(bookTeacherOfficeHours as any, bookTeacherOfficeHoursSaga),
        takeLatest(fetchMyHostOfficeHourBookings as any, fetchHostBookingsSaga),
        takeLatest(cancelMyHostOfficeHourBooking as any, cancelHostBookingSaga),
        takeLatest(fetchMyStudentOfficeHourBookings as any, fetchStudentBookingsSaga),
        takeLatest(cancelMyStudentOfficeHourBooking as any, cancelStudentBookingSaga),
        takeLatest(rescheduleMyStudentOfficeHourBooking as any, rescheduleStudentBookingSaga),
        takeLatest(requestMyStudentOfficeHourRefund as any, requestStudentRefundSaga),
    ]);
}
